import React, { useCallback, useRef } from 'react';

import appActions from 'common/support/applicationActions';
import focusManager from 'support/focus/FocusManager';

import Focusable from './Focusable';

interface FocusableRenderProps {
  hasFocus: boolean;
}

interface SelectableProps {
  render: (props: FocusableRenderProps) => React.ReactNode;
  onSelect?: (() => void) | null;
  onAction?: (action: string) => boolean | string;
  ttsText?: string | null;
  [key: string]: unknown;
}

const Selectable = ({
  onSelect = () => {},
  onAction = () => false,
  ttsText = '',
  render,
  ...rest
}: SelectableProps) => {
  const onActionOverride = useCallback(
    (action: string): boolean | string => {
      if (action === appActions.select) {
        onSelect?.();
        return true;
      }

      return onAction(action);
    },
    [onSelect, onAction],
  );

  const ref = useRef<HTMLDivElement|undefined>();
  const onClick = useCallback(() => {
    if (ref.current) {
      const focusId = ref.current.getAttribute('data-fcsid');

      if (focusManager.currentFocusId !== focusId) {
        focusManager.setCurrentFocus(focusId);
      }
    }
  }, []);

  return (
    <Focusable
      onAction={onActionOverride}
      render={render}
      ref={ref as React.RefObject<HTMLDivElement>}
      onClick={onClick}
      ttsText={ttsText}
      {...rest}
    />
  );
};

export default React.memo(Selectable);
